import { useEffect, useState } from 'react';
import { Link, Navigate, useBlocker, useParams } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import type { Song } from '../../shared/api/types';
import { Alert, Button, Card, Field, Loading } from '../../shared/ui/components';
import { SearchSelect } from '../../shared/ui/SearchSelect';
import { useAsync } from '../../shared/ui/useAsync';

/** La página aparte «Cantos del culto» se unió a la pestaña Alabanza del culto: las direcciones de antes llevan ahí. */
export function ServiceSongsPage() {
  const { serviceId = '' } = useParams();
  return <Navigate to={`/app/cultos/${serviceId}?ver=alabanza`} replace />;
}

export type SetlistItem = { songId: string; title: string; key: string; notes: string };

/**
 * Elegir, ordenar y poner el tono de los cantos (de un culto o de un ensayo). Cada canto en su propia
 * tarjeta (en el celular no se aprieta) y avisa si se sale con cambios sin guardar.
 */
export function SetlistEditor({
  churchId,
  url,
  title,
  initial,
  hint,
  onDone,
}: {
  churchId: string;
  /** A dónde se guardan (PUT { items }). */
  url: string;
  title: string;
  initial: SetlistItem[];
  /** Texto bajo el botón de guardar (p. ej. qué pasa después). */
  hint?: string;
  onDone: (saved: boolean) => void;
}) {
  const library = useAsync((signal) => api.get<{ items: Song[] }>(`/worship/churches/${churchId}/songs`, signal).then((r) => r.items), [churchId]);
  const [items, setItems] = useState(initial);
  const [saved] = useState(() => JSON.stringify(initial));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dirty = JSON.stringify(items) !== saved && !busy;
  useUnsavedWarning(dirty);
  const songs = library.status === 'ready' ? library.data : [];
  const move = (i: number, by: number) =>
    setItems((l) => {
      const to = i + by;
      if (to < 0 || to >= l.length) return l;
      const copy = [...l];
      [copy[i], copy[to]] = [copy[to]!, copy[i]!];
      return copy;
    });
  const set = (i: number, patch: Partial<SetlistItem>) => setItems((l) => l.map((x, j) => (j === i ? { ...x, ...patch } : x)));

  async function save() {
    setBusy(true);
    setError(null);
    try {
      await api.put(url, { items: items.map((i) => ({ songId: i.songId, key: i.key.trim() || null, notes: i.notes.trim() || null })) });
      onDone(true);
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  }

  return (
    <Card title={title}>
      {error ? <Alert>{error}</Alert> : null}
      {library.status === 'loading' ? <Loading /> : null}
      {library.status === 'ready' && songs.length === 0 ? (
        <p className="muted">
          La biblioteca de cantos está vacía. Agrégalos en <Link to="/app/cantos">Cantos</Link>.
        </p>
      ) : null}
      {items.length === 0 ? <p className="muted">Todavía no hay cantos elegidos.</p> : null}
      <ol className="list">
        {items.map((it, i) => {
          const song = songs.find((x) => x.id === it.songId);
          return (
            <li key={`${it.songId}-${i}`} className="list__item list__item--stack">
              <strong>
                {i + 1}. {it.title}
              </strong>
              {song ? (
                <span className="muted small">
                  Tono original {song.key ?? '—'}
                  {song.bpm ? ` · ${song.bpm} BPM` : ''}
                  {song.version ? ` · ${song.version}` : ''}
                </span>
              ) : null}
              <div className="form__row">
                <Field label={`Tono de ${it.title}`} value={it.key} maxLength={12} onChange={(e) => set(i, { key: e.target.value })} placeholder="Ej. G" />
                <Field label={`Nota de ${it.title}`} value={it.notes} maxLength={300} onChange={(e) => set(i, { notes: e.target.value })} placeholder="Ej. Adoración, ofrenda" />
              </div>
              <div className="actions actions--wrap">
                <Button type="button" variant="ghost" onClick={() => move(i, -1)} disabled={i === 0} aria-label={`Subir ${it.title}`}>
                  ↑ Subir
                </Button>
                <Button type="button" variant="ghost" onClick={() => move(i, 1)} disabled={i === items.length - 1} aria-label={`Bajar ${it.title}`}>
                  ↓ Bajar
                </Button>
                <Button type="button" variant="ghost" onClick={() => setItems((l) => l.filter((_, j) => j !== i))} aria-label={`Quitar ${it.title}`}>
                  Quitar
                </Button>
              </div>
            </li>
          );
        })}
      </ol>
      {songs.length ? (
        <div className="field">
          <label htmlFor="setlist-add">Agregar canto</label>
          <SearchSelect
            id="setlist-add"
            value=""
            placeholder="Escribe el nombre del canto…"
            options={songs.map((s) => ({ value: s.id, label: s.title, ...(s.author ? { hint: s.author } : {}) }))}
            onChange={(id) => {
              const s = songs.find((x) => x.id === id);
              if (s) setItems((l) => [...l, { songId: s.id, title: s.title, key: s.key ?? '', notes: '' }]);
            }}
          />
        </div>
      ) : null}
      <div className="actions">
        <Button busy={busy} onClick={() => void save()}>
          Guardar cantos
        </Button>
        <Button variant="ghost" onClick={() => (!dirty || window.confirm('Tienes cambios sin guardar en los cantos. ¿Descartarlos?')) && onDone(false)}>
          Cancelar
        </Button>
      </div>
      {hint ? <p className="small muted">{hint}</p> : null}
    </Card>
  );
}

/** Con cambios sin guardar, pregunta antes de salir (a otra página, otra pestaña del culto o al cerrar). */
function useUnsavedWarning(dirty: boolean) {
  const blocker = useBlocker(({ currentLocation: a, nextLocation: b }) => dirty && (a.pathname !== b.pathname || a.search !== b.search));
  useEffect(() => {
    if (blocker.state !== 'blocked') return;
    if (window.confirm('Tienes cambios sin guardar en los cantos. ¿Salir y perderlos?')) blocker.proceed();
    else blocker.reset();
  }, [blocker]);
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);
}
